import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FieldsetModule } from 'primeng/fieldset';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FormBuilderService } from './form-builder.service';
import { CardModule } from 'primeng/card';
import { IFormSearchLupa } from '@/shared/components/general/lupas/interfaces/lupas.interfaces';
import { SelectModule } from 'primeng/select';
import {
  IRealizarPqrs,
  IRealizarPqrsForm,
} from '../../interfaces/consulta-externa-pqrs.interface';
import { FooterModalService } from '@/shared/components/general/footer-modal/footer-modal.service';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { IResponseApiGeneric } from '@/interface/general/IResponseApi';
import { finalize } from 'rxjs';
import { RealizarPqrsService } from './realizar-pqrs.service';
import { CheckboxModule } from 'primeng/checkbox';
import { ModalGenericComponent } from '@/shared/components/general/modal-generic/modal-generic.component';

@Component({
  selector: 'app-realizar-pqrs',
  standalone: true,
  imports: [
    // Angular modules
    CommonModule,
    ReactiveFormsModule,
    // PrimeNG modules
    DatePickerModule,
    InputNumberModule,
    FieldsetModule,
    ButtonModule,
    FileUploadModule,
    SelectModule,
    IconFieldModule,
    InputTextModule,
    InputIconModule,
    CardModule,
    InputTextModule,
    CheckboxModule,
  ],
  templateUrl: './realizar-pqrs.component.html',
  styleUrl: './realizar-pqrs.component.scss',
})
export class RealizarPqrsComponent {
  buildForm!: FormGroup<IRealizarPqrsForm>;

  initialService = inject(RealizarPqrsService);
  footerService = inject(FooterModalService);
  dialogConfig = inject(DynamicDialogConfig);
  formService = inject(FormBuilderService);
  dialogService = inject(DialogService);
  dialogRef = inject(DynamicDialogRef);
  loading = false;
  archivoPrincipal: File | null = null;

  constructor(private messageService: MessageService) {
    this.initialService.loadInitialData();
    this.buildForm = this.formService.buildForm();

    const numero = this.dialogConfig.data?.NumeroDocumento;

    if (numero) {
      this.buildForm.patchValue({
        NumeroDocumento: numero,
      });
    }
  }

  private idFormulario = 'form_PQR';
  CodGlobalLupa: IFormSearchLupa = {
    idFormulario: this.idFormulario,
    campo: 'CodGlobal',
    label: 'Empresa',
    required: true,
  };
  CodTipoPqrLupa: IFormSearchLupa = {
    idFormulario: this.idFormulario,
    campo: 'CodTipoPqr',
    label: 'Tipo de Petición',
    required: true,
  };

  get isDarkMode(): boolean {
    return document.documentElement.classList.contains('dark');
  }
  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.archivoPrincipal = input.files[0];

      const reader = new FileReader();
      reader.onload = () => {
        let base64 = reader.result as string;

        if (base64.includes(',')) {
          base64 = base64.split(',')[1];
        }

        this.buildForm.patchValue({
          fileBase64DocPqr: base64,
        });
      };
      reader.readAsDataURL(this.archivoPrincipal);
    } else {
      this.archivoPrincipal = null;
      this.buildForm.patchValue({
        fileBase64DocPqr: null,
      });
    }
  }

  onGuardar(): void {
  if (this.buildForm.invalid) {
    this.buildForm.markAllAsTouched();
    return;
  }
 
  this.loading = true;
 
  const v = this.buildForm.value;
 
  const payload: IRealizarPqrs = {
    CodTipoPqr: v.CodTipoPqr ?? null,
    Asunto: v.Asunto ?? null,
    Descripcion: v.Descripcion ?? null,
    fileBase64DocPqr: v.fileBase64DocPqr ?? null,
 
    idPqrs: v.idPqrs ?? null,
    codPeticionario: v.codPeticionario ?? null,
    NumeroDocumento: v.NumeroDocumento ?? null,
    codUserUpdate: v.codUserUpdate ?? '',
    CodGlobal: v.CodGlobal ?? null,
    AutorizoDatos: v.AutorizoDatos ?? null,
  };
 
  this.initialService
    .postCreate(payload)
    .pipe(
      finalize(() => {
        this.loading = false;
      }),
    )
    .subscribe({
      next: (response: IResponseApiGeneric<any>) => {
 
        const isDarkMode =
          document.documentElement.classList.contains('dark');
 
        const codigoRadicado =
          response?.data?.CodRadicado ||
          response?.data?.codigoRadicado ||
          response?.data ||
          'N/A';
 
        const ref = this.dialogService.open(ModalGenericComponent, {
          header: 'Registro Guardado',
          width: '350px',
          style: { margin: 0, padding: 0 },
          modal: true,
          closable: true,
          maximizable: false,
          dismissableMask: true,
 
          contentStyle: {
            margin: 0,
            padding: 0,
            borderRadius: 0,
            background: isDarkMode ? '#111827' : '#ffffff',
            color: isDarkMode ? '#ffffff' : '#000000',
            opacity: '1',
          },
 
          data: {
            title: '¡Registro Exitoso!',
            message:
              `El registro fue guardado correctamente.\n\n` +
              `Código de radicado: ${codigoRadicado}`,
            tipoConfirmacion: 'success',
            cancelLabel: 'Cerrar',
          },
        });
 
        ref.onClose.subscribe(() => {
 
          // cerrar modal principal y refrescar tabla
          this.dialogRef.close({
            recargar: true,
            data: response,
          });
 
        });
      },
 
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            err?.error?.mensaje ||
            'Ocurrió un error al guardar el registro',
          life: 5000,
        });
      },
    });
}

  onCancelar(): void {
    try {
      this.dialogRef.close({ recargar: false });
    } catch (e) {
      // fallback si no tienes DynamicDialogRef
      console.warn('dialogRef no disponible para cerrar el modal', e);
    }
  }
}
